import type { CorrectionInfo, ReceiptDocType, SettlementType } from '@core/db/models'
import type { FiscalReceiptInput } from './FiscalService'

/** JSON-задание ATOL DTO 10 для processJson(). */
export interface AtolReceiptTask {
  type: string
  operator: { name: string }
  electronically: boolean
  items: Array<{
    type: 'position'
    name: string
    price: number
    quantity: number
    amount: number
    measurementUnit?: 'piece' | 'kilogram'
    paymentMethod: string
    paymentObject: string
    tax: { type: 'none' }
    /** ФФД >= 1.2: тег 2000 передаётся через imcParams, не через markingCode. */
    imcParams?: {
      imcType: 'auto'
      imc: string
      itemEstimatedStatus:
        | 'itemPieceSold'
        | 'itemPieceReturn'
        | 'itemDrySold'
        | 'itemDryReturn'
      imcModeProcessing: 0
    }
  }>
  payments: Array<{ type: 'cash' | 'electronically'; sum: number }>
  total: number
  correction?: {
    correctionType: 'self' | 'instruction'
    correctionBaseDate: string
    correctionBaseNumber: string
    correctionBaseDescription?: string
    correctionBaseDocument?: string
  }
}

const BASE_TYPE: Record<SettlementType, string> = {
  income: 'sell',
  income_return: 'sellReturn',
  outcome: 'buy',
  outcome_return: 'buyReturn'
}

function docType(docType: ReceiptDocType | undefined, settlement: SettlementType | undefined): string {
  const base = BASE_TYPE[settlement ?? 'income'] ?? 'sell'
  return docType === 'correction' ? `${base}Correction` : base
}

function money(value: number): number {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100
}

function measurementUnit(unit?: string): 'piece' | 'kilogram' | undefined {
  if (!unit) return undefined
  if (unit === 'кг' || unit === 'kg') return 'kilogram'
  return 'piece'
}

/** Тег 2000 в DTO 10 передаётся base64-строкой, GS и регистр сохраняются. */
function base64(value: string): string {
  // Код маркировки — ASCII + GS; btoa достаточно и не требует TextEncoder
  // в старых Android WebView.
  return btoa(value)
}

function estimatedStatus(
  settlement: SettlementType | undefined,
  weight: boolean
): 'itemPieceSold' | 'itemPieceReturn' | 'itemDrySold' | 'itemDryReturn' {
  const isReturn = settlement === 'income_return' || settlement === 'outcome_return'
  if (isReturn) return weight ? 'itemDryReturn' : 'itemPieceReturn'
  return weight ? 'itemDrySold' : 'itemPieceSold'
}

function correctionPayload(info?: CorrectionInfo): AtolReceiptTask['correction'] | undefined {
  if (!info) return undefined
  return {
    correctionType: info.kind,
    correctionBaseDate: info.baseDate.replace(/-/g, '.'),
    correctionBaseNumber: info.baseNumber,
    correctionBaseDescription: info.basis,
    correctionBaseDocument: info.instructionNumber
  }
}

/**
 * Собрать ATOL JSON для продажи/возврата/коррекции.
 *
 * Магазин: Патент, одна СНО, без НДС. Поэтому tax.type = none,
 * taxationType не передаём.
 */
export function buildAtolReceiptTask(input: FiscalReceiptInput): AtolReceiptTask {
  if (!Number.isFinite(input.total_amount) || input.total_amount <= 0) {
    throw new Error('Сумма чека должна быть больше нуля')
  }
  const isCorrection = input.docType === 'correction'
  if (!Array.isArray(input.items) || (input.items.length === 0 && !isCorrection)) {
    throw new Error('Чек не содержит позиций')
  }
  const items = input.items.map((item) => {
    const amount = money(Number(item.price) * Number(item.quantity))
    if (
      !item.name.trim() ||
      !Number.isFinite(item.price) ||
      item.price < 0 ||
      !Number.isFinite(item.quantity) ||
      item.quantity <= 0
    ) {
      throw new Error('Некорректная позиция чека')
    }
    const unit = measurementUnit(item.unit)
    const rawMark = item.marking_code
    if (rawMark != null && rawMark.trim() === '') {
      throw new Error('Пустой код маркировки')
    }
    const mark = rawMark?.trim()
    return {
      type: 'position' as const,
      name: item.name,
      price: money(item.price),
      quantity: item.quantity,
      amount,
      measurementUnit: unit,
      paymentMethod: 'fullPayment',
      paymentObject: mark ? 'commodityWithMarking' : 'commodity',
      tax: { type: 'none' as const },
      imcParams: mark
        ? {
            imcType: 'auto' as const,
            imc: base64(mark),
            itemEstimatedStatus: estimatedStatus(
              input.settlementType,
              unit === 'kilogram'
            ),
            imcModeProcessing: 0 as const
          }
        : undefined
    }
  })

  const isCash = input.paymentMethod === 'cash' || input.paymentMethod == null
  return {
    type: docType(input.docType, input.settlementType),
    operator: { name: input.cashierName || 'Кассир' },
    electronically: false,
    items,
    payments: [{ type: isCash ? 'cash' : 'electronically', sum: money(input.total_amount) }],
    total: money(input.total_amount),
    correction: correctionPayload(input.correction)
  }
}
